import React from 'react';
import { Link } from 'react-router-dom';
import { differenceInCalendarDays, e4Format, e4Formats, isPast } from 'helpers/dates';

import CompetencyList from 'components/general/competencyList';
import { Details, SystemText } from '@salesperformanceint/e4shared';
import PokeModal from '../pokeModal';

export const TaskSummary = ({ task, canPoke, canViewPathways }) => {
    const [showPoke, setShowPoke] = React.useState(false);
    const [parent, setParent] = React.useState(null);

    const listParent = React.useRef(null);

    React.useEffect(() => {
        if (listParent?.current) {
            setParent(listParent?.current?.children[0]);
        }
    }, [listParent?.current]);

    const text = {
        early: x => SystemText('Objective/Summary/Early', x),
        late: x => SystemText('Objective/Summary/Late', x),
        pathway: SystemText('Objective/Summary/Pathway'),
        originator: SystemText('Objective/Summary/Originator'),
        assigned: SystemText('Objective/Summary/Assigned'),
        type: SystemText('Objective/Summary/Type'),
        system: SystemText('Objective/Summary/System'),
        due: SystemText('Objective/Summary/Due'),
        overdue: SystemText('Objective/Summary/Overdue'),
        poke: SystemText('Objective/Summary/Poke'),
        requiresApproval: SystemText('Objective/Summary/RequiresApproval'),
        requiresApprovalNo: SystemText('Objective/Summary/RequiresApproval/No'),
        requiresApprovalYes: SystemText('Objective/Summary/RequiresApproval/Yes'),
        approved: SystemText('Objective/Summary/Approved'),
        notAchieved: SystemText('Objective/Summary/NotAchieved'),
        completed: SystemText('Objective/Summary/Completed'),
        competencies: SystemText('Objective/Summary/Competencies'),
    };

    const originator = task.course_Id ? task.course : task.owner_Fullname ? task.owner_Fullname : text.system;

    const generateOnTime = (completed, due) => {
        const diff = differenceInCalendarDays(completed, due);
        return diff > 0 ? (
            <label className="label label-danger">
                <i className="mr5 fa fa-warning" />
                <span>{text.late(diff)}</span>
            </label>
        ) : (
            <label className="label label-success">
                <i className="mr5 fa fa-thumbs-up" />
                <span>{text.early(diff * -1)}</span>
            </label>
        );
    };

    const completedExtra = () => {
        return (
            <>
                <span className="mr5">{e4Format(task.date_Completed, e4Formats.long)}</span>
                {generateOnTime(task.date_Completed, task.due)}
            </>
        );
    };

    const dueExtra = () => {
        return (
            <>
                {(!isPast(task.due) || !canPoke) && <span className="mr5">{e4Format(task.due, e4Formats.long)}</span>}
                {isPast(task.due) ? (
                    <span>
                        <label
                            className="label label-danger mr5"
                            title={`${text.due}: ${e4Format(task.due, e4Formats.long)}`}
                        >
                            <i className="fa fa-warning mr5" />
                            {text.overdue}
                        </label>
                        {canPoke && (
                            <button id="poke-button" onClick={() => setShowPoke(true)} className="btn btn-primary btn-xs">
                                <i className="fa fa-bell-ring mr5" />
                                {text.poke}
                            </button>
                        )}
                    </span>
                ) : null}
            </>
        );
    };

    const approvalExtra = () => {
        return (
            <>
                <span className="mr5">{task.requires_Manager ? text.requiresApprovalYes : text.requiresApprovalNo}</span>
                {task.requires_Manager && task.approved && (
                    <>
                        {task.metric_Task && !task.goal_Met ? (
                            <label className="label label-danger">
                                <i className="fa fa-times mr5" />
                                {text.notAchieved}
                            </label>
                        ) : (
                            <label className="label label-success">
                                <i className="fa fa-check-circle mr5" />
                                {text.approved}
                            </label>
                        )}
                    </>
                )}
            </>
        );
    };

    const dueCompleted = task.date_Completed
        ? { title: text.completed, value: completedExtra, contentNoWrap: true }
        : { title: text.due, value: dueExtra, titleNoWrap: true, contentNoWrap: true };

    const pathwayOriginator = task.pathway_Id
        ? {
              title: text.pathway,
              value: () =>
                  canViewPathways ? (
                      <Link to={`/develop/pathways/${task.profile_Id}?id=${task.pathway_Assignment_Id}`}>
                          {task.pathway}
                      </Link>
                  ) : (
                      <span>{task.pathway}</span>
                  ),
          }
        : { title: text.originator, value: originator };

    const mainDetails = [
        dueCompleted,
        {
            title: text.assigned,
            value: e4Format(task.date_Assigned, e4Formats.long),
            titleNoWrap: true,
            contentNoWrap: true,
        },
        { title: text.type, value: SystemText(`Objective/Type/${task.taskType}`) },
        pathwayOriginator,
        { title: text.requiresApproval, value: approvalExtra, titleNoWrap: true },
    ];

    const extraDetails = [
        {
            title: text.competencies,
            value: () => <CompetencyList competencies={task.competencies} parent={parent} />,
        },
    ];

    return (
        <>
            <div className={`col-xs-12`}>
                <Details items={mainDetails} />
            </div>
            {task.competencies?.length > 0 && (
                <div className="col-xs-12" ref={listParent}>
                    <Details items={extraDetails} />
                </div>
            )}
            <PokeModal show={showPoke} task={task} close={() => setShowPoke(false)} />
        </>
    );
};

export default TaskSummary;
